import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { StatusBadge } from '../components/StatusBadge';
import { PageLoader, ButtonLoader, SkeletonTable } from '../components/LoadingSpinner';
import { adminAPI, assetsAPI } from '../lib/api';
import { formatCurrency, formatNumber, formatDateTime, formatDate } from '../lib/formatters';
import { Plus, Edit, Check, X, TrendingUp, TrendingDown, DollarSign, Calendar, Settings, AlertCircle, BarChart3, Zap, Globe, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('assets');
  const [assets, setAssets] = useState([]);
  const [requests, setRequests] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Asset form state
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [assetForm, setAssetForm] = useState({
    name: '',
    unit_number: '',
    type: 'truck',
    description: '',
    total_shares: 100,
    current_price: 1000,
    status: 'available',
    image_url: '',
    allow_buy_requests: true,
  });
  
  // Performance state
  const [selectedAsset, setSelectedAsset] = useState('');
  const [performances, setPerformances] = useState([]);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [showPerformanceForm, setShowPerformanceForm] = useState(false);
  const [performanceForm, setPerformanceForm] = useState({
    asset_id: '',
    date: '',
    earnings_total: '',
    expense_eld: '',
    expense_fuel: '',
    expense_insurance: '',
    expense_tolls: '',
    expense_driver_pay: '',
    expense_depreciation: '',
    expense_other: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Pricing state
  const [pricingConfig, setPricingConfig] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [showPriceOverride, setShowPriceOverride] = useState(false);
  const [priceOverrideForm, setPriceOverrideForm] = useState({
    new_price: '',
    reason: '',
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  useEffect(() => {
    if (selectedAsset && (activeTab === 'performance' || activeTab === 'pricing')) {
      if (activeTab === 'performance') {
        fetchPerformanceData(selectedAsset);
      } else {
        fetchPricingConfig(selectedAsset);
      }
    }
  }, [selectedAsset, activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'assets' || activeTab === 'performance' || activeTab === 'pricing') {
        const response = await assetsAPI.list({});
        setAssets(response.data);
        if (response.data.length > 0 && !selectedAsset) {
          setSelectedAsset(response.data[0].asset_id);
        }
      } else if (activeTab === 'requests') {
        const response = await adminAPI.listRequests({});
        setRequests(response.data);
      } else if (activeTab === 'audit') {
        const response = await adminAPI.getAuditLogs({});
        setAuditLogs(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformanceData = async (assetId) => {
    try {
      setPerformanceLoading(true);
      const response = await adminAPI.getPerformance(assetId, 12);
      setPerformances(response.data);
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
      toast.error('Failed to load performance data');
    } finally {
      setPerformanceLoading(false);
    }
  };

  const fetchPricingConfig = async (assetId) => {
    try {
      setPricingLoading(true);
      const response = await adminAPI.getPricingConfig(assetId);
      setPricingConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch pricing config:', error);
      toast.error('Failed to load pricing configuration');
    } finally {
      setPricingLoading(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await adminAPI.updateRequest(requestId, { status: 'approved' });
      toast.success('Request approved successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to approve request:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await adminAPI.updateRequest(requestId, { status: 'rejected' });
      toast.success('Request rejected');
      fetchData();
    } catch (error) {
      console.error('Failed to reject request:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    }
  };

  // Asset CRUD handlers
  const openAddAssetModal = () => {
    setEditingAsset(null);
    setAssetForm({
      name: '',
      unit_number: '',
      type: 'truck',
      description: '',
      total_shares: 100,
      current_price: 1000,
      status: 'available',
      image_url: '',
      allow_buy_requests: true,
    });
    setShowAssetModal(true);
  };

  const openEditAssetModal = (asset) => {
    setEditingAsset(asset);
    setAssetForm({
      name: asset.name || '',
      unit_number: asset.unit_number || '',
      type: asset.type || 'truck',
      description: asset.description || '',
      total_shares: asset.total_shares || 100,
      current_price: asset.current_price || 1000,
      status: asset.status || 'available',
      image_url: asset.image_url || '',
      allow_buy_requests: asset.allow_buy_requests !== false,
    });
    setShowAssetModal(true);
  };

  const handleSaveAsset = async (e) => {
    e.preventDefault();
    
    if (!assetForm.name || !assetForm.unit_number) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingAsset) {
        // Update existing asset
        await adminAPI.updateAsset(editingAsset.asset_id, assetForm);
        toast.success('Asset updated successfully');
      } else {
        // Create new asset
        await adminAPI.createAsset(assetForm);
        toast.success('Asset created successfully');
      }
      
      setShowAssetModal(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save asset:', error);
      toast.error(error.response?.data?.detail || 'Failed to save asset');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset? This action cannot be undone.')) {
      return;
    }

    try {
      await adminAPI.deleteAsset(assetId);
      toast.success('Asset deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete asset:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete asset');
    }
  };

  const handleAddPerformance = async (e) => {
    e.preventDefault();
    if (!performanceForm.asset_id || !performanceForm.date || !performanceForm.earnings_total) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      await adminAPI.addPerformance({
        asset_id: performanceForm.asset_id,
        date: performanceForm.date,
        earnings_total: parseFloat(performanceForm.earnings_total) || 0,
        expense_eld: parseFloat(performanceForm.expense_eld) || 0,
        expense_fuel: parseFloat(performanceForm.expense_fuel) || 0,
        expense_insurance: parseFloat(performanceForm.expense_insurance) || 0,
        expense_tolls: parseFloat(performanceForm.expense_tolls) || 0,
        expense_driver_pay: parseFloat(performanceForm.expense_driver_pay) || 0,
        expense_depreciation: parseFloat(performanceForm.expense_depreciation) || 0,
        expense_other: parseFloat(performanceForm.expense_other) || 0,
        notes: performanceForm.notes,
      });
      toast.success('Daily performance data added successfully');
      setShowPerformanceForm(false);
      setPerformanceForm({
        asset_id: selectedAsset,
        date: '',
        earnings_total: '',
        expense_eld: '',
        expense_fuel: '',
        expense_insurance: '',
        expense_tolls: '',
        expense_driver_pay: '',
        expense_depreciation: '',
        expense_other: '',
        notes: '',
      });
      fetchPerformanceData(selectedAsset);
    } catch (error) {
      console.error('Failed to add performance:', error);
      toast.error(error.response?.data?.detail || 'Failed to add performance data');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePricingConfig = async (updates) => {
    try {
      setSubmitting(true);
      await adminAPI.updatePricingConfig(selectedAsset, updates);
      toast.success('Pricing configuration updated');
      fetchPricingConfig(selectedAsset);
    } catch (error) {
      console.error('Failed to update pricing config:', error);
      toast.error(error.response?.data?.detail || 'Failed to update pricing configuration');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePriceOverride = async (e) => {
    e.preventDefault();
    if (!priceOverrideForm.new_price || !priceOverrideForm.reason) {
      toast.error('Please provide new price and reason');
      return;
    }

    try {
      setSubmitting(true);
      const result = await adminAPI.overridePrice(selectedAsset, {
        new_price: parseFloat(priceOverrideForm.new_price),
        reason: priceOverrideForm.reason,
      });
      toast.success(`Price updated! Change: ${result.data.change_percent.toFixed(2)}%`);
      setShowPriceOverride(false);
      setPriceOverrideForm({ new_price: '', reason: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to override price:', error);
      toast.error(error.response?.data?.detail || 'Failed to override price');
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { id: 'assets', label: 'Assets', icon: BarChart3 },
    { id: 'performance', label: 'Performance', icon: TrendingUp },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'requests', label: 'Requests', icon: Check },
    { id: 'audit', label: 'Audit', icon: AlertCircle },
  ];

  const selectedAssetData = assets.find(a => a.asset_id === selectedAsset);

  // Calculate performance summary (now daily)
  const performanceSummary = performances.length > 0 ? {
    totalEarnings: performances.reduce((sum, p) => sum + p.earnings_total, 0),
    totalExpenses: performances.reduce((sum, p) => sum + p.expenses_total, 0),
    totalProfit: performances.reduce((sum, p) => sum + p.net_profit, 0),
    avgDailyProfit: performances.reduce((sum, p) => sum + p.net_profit, 0) / performances.length,
    expenseBreakdown: {
      fee: performances.reduce((sum, p) => sum + (p.expense_fee || 0), 0),
      eld: performances.reduce((sum, p) => sum + (p.expense_eld || 0), 0),
      fuel: performances.reduce((sum, p) => sum + (p.expense_fuel || 0), 0),
      insurance: performances.reduce((sum, p) => sum + (p.expense_insurance || 0), 0),
      tolls: performances.reduce((sum, p) => sum + (p.expense_tolls || 0), 0),
      driver_pay: performances.reduce((sum, p) => sum + (p.expense_driver_pay || 0), 0),
      depreciation: performances.reduce((sum, p) => sum + (p.expense_depreciation || 0), 0),
      other: performances.reduce((sum, p) => sum + (p.expense_other || 0), 0),
    }
  } : null;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Header />

      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Page Title */}
        <div className="mb-8">
          {/* Out of Company Button - Mobile: Full width at top */}
          <button
            data-testid="out-of-company-btn"
            onClick={() => navigate('/public-exchange')}
            className="w-full md:w-auto md:float-right mb-4 md:mb-0 md:ml-4 flex items-center justify-center md:justify-start gap-3 px-5 py-3 md:px-6 md:py-4 bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-500 hover:from-purple-700 hover:via-blue-700 hover:to-cyan-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <Globe className="w-5 h-5 md:w-6 md:h-6" />
            <div className="text-left">
              <div className="text-xs md:text-sm opacity-80">Public Platform</div>
              <div className="text-base md:text-lg">Out of Company →</div>
            </div>
          </button>
          
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[hsl(var(--text-primary))] mb-2">
            Admin Panel
          </h1>
          <p className="text-sm md:text-base text-[hsl(var(--text-secondary))]">
            Manage assets, performance tracking, and pricing
          </p>
          <div className="clear-both"></div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-[hsl(var(--border))]">
            <nav className="flex gap-4 md:gap-8 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  data-testid={`admin-tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary))]'
                      : 'border-transparent text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))]'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <PageLoader text="Loading admin data..." />
        ) : (
          <>
            {/* Assets Tab */}
            {activeTab === 'assets' && (
              <div className="space-y-4">
                {/* Reset Database Button */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex justify-between items-center">
                  <div>
                    <h3 className="text-sm font-semibold text-red-800">Reset Database</h3>
                    <p className="text-xs text-red-600">Delete all data and start fresh with TRK-1006 + TRL-001</p>
                  </div>
                  <button
                    data-testid="reset-database-btn"
                    onClick={async () => {
                      if (window.confirm('⚠️ WARNING: This will DELETE ALL data (assets, performance, ownership) and create fresh TRK-1006 and TRL-001. Continue?')) {
                        try {
                          setLoading(true);
                          await adminAPI.resetDatabase();
                          toast.success('Database reset successfully! Fresh TRK-1006 and TRL-001 created.');
                          fetchData();
                        } catch (error) {
                          console.error('Reset failed:', error);
                          toast.error('Failed to reset database');
                        } finally {
                          setLoading(false);
                        }
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors duration-200"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reset All
                  </button>
                </div>

                <div className="bg-white border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <div className="p-6 border-b border-[hsl(var(--border))] flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-[hsl(var(--text-primary))]">
                      All Assets ({assets.length})
                    </h2>
                    <button
                      data-testid="create-asset-button"
                      onClick={openAddAssetModal}
                      className="flex items-center gap-2 px-4 py-2 bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold rounded-lg transition-colors duration-200"
                    >
                      <Plus className="w-4 h-4" />
                      Add Asset
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table data-testid="admin-assets-table" className="w-full">
                      <thead className="bg-[hsl(var(--background-secondary))]">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Asset</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Shares</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[hsl(var(--border))]">
                      {assets.map((asset) => (
                        <tr key={asset.asset_id} className="hover:bg-[hsl(var(--background-secondary))]">
                          <td className="px-6 py-4">
                            <div className="font-medium text-[hsl(var(--text-primary))]">{asset.name}</div>
                            <div className="text-xs font-mono text-[hsl(var(--text-tertiary))]">#{asset.unit_number}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-[hsl(var(--text-primary))]">{asset.type.toUpperCase()}</td>
                          <td className="px-6 py-4 text-sm font-mono font-semibold text-[hsl(var(--text-primary))]">{formatCurrency(asset.current_price)}</td>
                          <td className="px-6 py-4 text-sm font-mono text-[hsl(var(--text-primary))]">{asset.sold_shares} / {asset.total_shares}</td>
                          <td className="px-6 py-4"><StatusBadge status={asset.status} /></td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <button 
                                data-testid={`edit-asset-${asset.asset_id}`} 
                                onClick={() => openEditAssetModal(asset)}
                                className="p-1 hover:bg-[hsl(var(--background-secondary))] rounded"
                                title="Edit Asset"
                              >
                                <Edit className="w-4 h-4 text-[hsl(var(--brand-primary))]" />
                              </button>
                              <button 
                                data-testid={`delete-asset-${asset.asset_id}`} 
                                onClick={() => handleDeleteAsset(asset.asset_id)}
                                className="p-1 hover:bg-red-100 rounded"
                                title="Delete Asset"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            )}

            {/* Performance Tab */}
            {activeTab === 'performance' && (
              <div className="space-y-6">
                {/* Asset Selector */}
                <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-6">
                  <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">Select Asset</label>
                  <select
                    data-testid="performance-asset-selector"
                    value={selectedAsset}
                    onChange={(e) => setSelectedAsset(e.target.value)}
                    className="w-full md:w-96 px-4 py-3 border border-[hsl(var(--border))] rounded-lg bg-white"
                  >
                    {assets.map((asset) => (
                      <option key={asset.asset_id} value={asset.asset_id}>
                        {asset.unit_number} - {asset.name}
                      </option>
                    ))}
                  </select>
                  {selectedAssetData && (
                    <div className="mt-3 flex items-center gap-4 text-sm text-[hsl(var(--text-secondary))]">
                      <span className="px-2 py-1 bg-[hsl(var(--background-secondary))] rounded font-medium">{selectedAssetData.type.toUpperCase()}</span>
                      <span>Current Price: <strong className="text-[hsl(var(--text-primary))]">{formatCurrency(selectedAssetData.current_price)}</strong></span>
                    </div>
                  )}
                </div>

                {/* Performance Summary */}
                {performanceSummary && (
                  <div className="space-y-4">
                    {/* Main Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-6">
                        <div className="flex items-center gap-2 text-sm text-[hsl(var(--text-secondary))] mb-1">
                          <TrendingUp className="w-4 h-4 text-green-500" />
                          Total Earnings
                        </div>
                        <div className="text-2xl font-bold font-mono text-green-600">{formatCurrency(performanceSummary.totalEarnings)}</div>
                      </div>
                      <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-6">
                        <div className="flex items-center gap-2 text-sm text-[hsl(var(--text-secondary))] mb-1">
                          <TrendingDown className="w-4 h-4 text-red-500" />
                          Total Expenses
                        </div>
                        <div className="text-2xl font-bold font-mono text-red-600">{formatCurrency(performanceSummary.totalExpenses)}</div>
                      </div>
                      <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-6">
                        <div className="flex items-center gap-2 text-sm text-[hsl(var(--text-secondary))] mb-1">
                          <DollarSign className="w-4 h-4 text-[hsl(var(--brand-primary))]" />
                          Net Profit
                        </div>
                        <div className={`text-2xl font-bold font-mono ${performanceSummary.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(performanceSummary.totalProfit)}
                        </div>
                      </div>
                      <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-6">
                        <div className="flex items-center gap-2 text-sm text-[hsl(var(--text-secondary))] mb-1">
                          <BarChart3 className="w-4 h-4 text-[hsl(var(--brand-primary))]" />
                          Avg Daily
                        </div>
                        <div className={`text-2xl font-bold font-mono ${performanceSummary.avgDailyProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(performanceSummary.avgDailyProfit)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expense Breakdown */}
                    <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-6">
                      <h4 className="text-sm font-semibold text-[hsl(var(--text-primary))] mb-4">Expense Breakdown</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
                        <div className="text-center p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="text-xs text-amber-700 mb-1 font-semibold">Fee (12%)</div>
                          <div className="text-sm font-mono font-bold text-amber-600">{formatCurrency(performanceSummary.expenseBreakdown.fee)}</div>
                        </div>
                        <div className="text-center p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                          <div className="text-xs text-[hsl(var(--text-secondary))] mb-1">ELD</div>
                          <div className="text-sm font-mono font-semibold text-red-600">{formatCurrency(performanceSummary.expenseBreakdown.eld)}</div>
                        </div>
                        <div className="text-center p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                          <div className="text-xs text-[hsl(var(--text-secondary))] mb-1">Fuel</div>
                          <div className="text-sm font-mono font-semibold text-red-600">{formatCurrency(performanceSummary.expenseBreakdown.fuel)}</div>
                        </div>
                        <div className="text-center p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                          <div className="text-xs text-[hsl(var(--text-secondary))] mb-1">Insurance</div>
                          <div className="text-sm font-mono font-semibold text-red-600">{formatCurrency(performanceSummary.expenseBreakdown.insurance)}</div>
                        </div>
                        <div className="text-center p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                          <div className="text-xs text-[hsl(var(--text-secondary))] mb-1">Tolls</div>
                          <div className="text-sm font-mono font-semibold text-red-600">{formatCurrency(performanceSummary.expenseBreakdown.tolls)}</div>
                        </div>
                        <div className="text-center p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                          <div className="text-xs text-[hsl(var(--text-secondary))] mb-1">Driver Pay</div>
                          <div className="text-sm font-mono font-semibold text-red-600">{formatCurrency(performanceSummary.expenseBreakdown.driver_pay)}</div>
                        </div>
                        <div className="text-center p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                          <div className="text-xs text-[hsl(var(--text-secondary))] mb-1">Depreciation</div>
                          <div className="text-sm font-mono font-semibold text-red-600">{formatCurrency(performanceSummary.expenseBreakdown.depreciation)}</div>
                        </div>
                        <div className="text-center p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                          <div className="text-xs text-[hsl(var(--text-secondary))] mb-1">Other</div>
                          <div className="text-sm font-mono font-semibold text-red-600">{formatCurrency(performanceSummary.expenseBreakdown.other)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Add Performance Button */}
                <div className="flex justify-end">
                  <button
                    data-testid="add-performance-btn"
                    onClick={() => {
                      // Get the selected asset's default values
                      const asset = assets.find(a => a.asset_id === selectedAsset);
                      setPerformanceForm({ 
                        ...performanceForm, 
                        asset_id: selectedAsset,
                        expense_eld: asset?.default_eld || '',
                        expense_depreciation: asset?.default_depreciation || '',
                        expense_fuel: '',
                        expense_insurance: '',
                        expense_tolls: '',
                        expense_driver_pay: '',
                        expense_other: '',
                        earnings_total: '',
                        date: '',
                        notes: ''
                      });
                      setShowPerformanceForm(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold rounded-lg transition-colors duration-200"
                  >
                    <Plus className="w-4 h-4" />
                    Add Daily Data
                  </button>
                </div>

                {/* Performance History */}
                {performanceLoading ? (
                  <SkeletonTable rows={5} cols={5} />
                ) : performances.length === 0 ? (
                  <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-12 text-center">
                    <BarChart3 className="w-12 h-12 text-[hsl(var(--text-tertiary))] mx-auto mb-4" />
                    <p className="text-[hsl(var(--text-secondary))]">No performance data yet</p>
                    <p className="text-sm text-[hsl(var(--text-tertiary))] mt-1">Add daily earnings and expenses to track unit performance</p>
                  </div>
                ) : (
                  <div className="bg-white border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                    <div className="p-6 border-b border-[hsl(var(--border))]">
                      <h3 className="text-lg font-semibold text-[hsl(var(--text-primary))]">Daily Performance History</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table data-testid="performance-table" className="w-full">
                        <thead className="bg-[hsl(var(--background-secondary))]">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Earnings</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-amber-600 uppercase">Fee 12%</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">ELD</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Fuel</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Insurance</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Tolls</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Driver</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Depr.</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Other</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Total Exp</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Net</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[hsl(var(--border))]">
                          {performances.map((perf) => (
                            <tr key={perf.performance_id} className="hover:bg-[hsl(var(--background-secondary))]">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-[hsl(var(--text-tertiary))]" />
                                  <span className="text-sm text-[hsl(var(--text-primary))] whitespace-nowrap">
                                    {formatDate(perf.date)}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm font-mono font-semibold text-green-600 whitespace-nowrap">
                                +{formatCurrency(perf.earnings_total)}
                              </td>
                              <td className="px-4 py-3 text-xs font-mono font-semibold text-amber-600 whitespace-nowrap">
                                {perf.expense_fee > 0 ? `-${formatCurrency(perf.expense_fee)}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-xs font-mono text-red-500 whitespace-nowrap">
                                {perf.expense_eld > 0 ? `-${formatCurrency(perf.expense_eld)}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-xs font-mono text-red-500 whitespace-nowrap">
                                {perf.expense_fuel > 0 ? `-${formatCurrency(perf.expense_fuel)}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-xs font-mono text-red-500 whitespace-nowrap">
                                {perf.expense_insurance > 0 ? `-${formatCurrency(perf.expense_insurance)}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-xs font-mono text-red-500 whitespace-nowrap">
                                {perf.expense_tolls > 0 ? `-${formatCurrency(perf.expense_tolls)}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-xs font-mono text-red-500 whitespace-nowrap">
                                {perf.expense_driver_pay > 0 ? `-${formatCurrency(perf.expense_driver_pay)}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-xs font-mono text-red-500 whitespace-nowrap">
                                {perf.expense_depreciation > 0 ? `-${formatCurrency(perf.expense_depreciation)}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-xs font-mono text-red-500 whitespace-nowrap">
                                {perf.expense_other > 0 ? `-${formatCurrency(perf.expense_other)}` : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm font-mono font-semibold text-red-600 whitespace-nowrap">
                                -{formatCurrency(perf.expenses_total)}
                              </td>
                              <td className={`px-4 py-3 text-sm font-mono font-bold whitespace-nowrap ${perf.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {perf.net_profit >= 0 ? '+' : ''}{formatCurrency(perf.net_profit)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Pricing Tab */}
            {activeTab === 'pricing' && (
              <div className="space-y-6">
                {/* Asset Selector */}
                <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-6">
                  <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">Select Asset</label>
                  <select
                    data-testid="pricing-asset-selector"
                    value={selectedAsset}
                    onChange={(e) => setSelectedAsset(e.target.value)}
                    className="w-full md:w-96 px-4 py-3 border border-[hsl(var(--border))] rounded-lg bg-white"
                  >
                    {assets.map((asset) => (
                      <option key={asset.asset_id} value={asset.asset_id}>
                        {asset.unit_number} - {asset.name}
                      </option>
                    ))}
                  </select>
                </div>

                {pricingLoading ? (
                  <PageLoader text="Loading pricing configuration..." />
                ) : pricingConfig && (
                  <>
                    {/* Current Price Card */}
                    <div className="bg-gradient-to-br from-[hsl(var(--brand-primary))] to-[hsl(var(--brand-primary-dark))] rounded-xl p-8 text-white">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-white/80 text-sm mb-1">Current Share Price</div>
                          <div className="text-5xl font-bold font-mono">{formatCurrency(selectedAssetData?.current_price || 0)}</div>
                          <div className="mt-2 flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${pricingConfig.pricing_mode === 'auto' ? 'bg-green-500' : 'bg-white/20'}`}>
                              {pricingConfig.pricing_mode === 'auto' ? 'AUTO PRICING' : 'MANUAL'}
                            </span>
                            {pricingConfig.is_frozen && (
                              <span className="px-2 py-1 bg-red-500 rounded text-xs font-semibold">FROZEN</span>
                            )}
                          </div>
                        </div>
                        <button
                          data-testid="override-price-btn"
                          onClick={() => setShowPriceOverride(true)}
                          className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors duration-200"
                        >
                          <Edit className="w-4 h-4" />
                          Override Price
                        </button>
                      </div>
                    </div>

                    {/* Pricing Configuration */}
                    <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-[hsl(var(--text-primary))] mb-6 flex items-center gap-2">
                        <Settings className="w-5 h-5" />
                        Pricing Configuration
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Pricing Mode Toggle */}
                        <div className="p-4 bg-[hsl(var(--background-secondary))] rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-[hsl(var(--text-primary))]">Auto-Pricing</label>
                            <button
                              data-testid="toggle-auto-pricing"
                              onClick={() => handleUpdatePricingConfig({ auto_pricing_enabled: !pricingConfig.auto_pricing_enabled })}
                              className={`w-12 h-6 rounded-full transition-colors duration-200 ${pricingConfig.auto_pricing_enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                            >
                              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${pricingConfig.auto_pricing_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                            </button>
                          </div>
                          <p className="text-xs text-[hsl(var(--text-secondary))]">
                            {pricingConfig.auto_pricing_enabled ? 'Price updates automatically based on performance' : 'Price is manually controlled'}
                          </p>
                        </div>

                        {/* Freeze Toggle */}
                        <div className="p-4 bg-[hsl(var(--background-secondary))] rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-[hsl(var(--text-primary))]">Freeze Price</label>
                            <button
                              data-testid="toggle-freeze"
                              onClick={() => handleUpdatePricingConfig({ is_frozen: !pricingConfig.is_frozen })}
                              className={`w-12 h-6 rounded-full transition-colors duration-200 ${pricingConfig.is_frozen ? 'bg-red-500' : 'bg-gray-300'}`}
                            >
                              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${pricingConfig.is_frozen ? 'translate-x-6' : 'translate-x-0.5'}`} />
                            </button>
                          </div>
                          <p className="text-xs text-[hsl(var(--text-secondary))]">
                            {pricingConfig.is_frozen ? 'Price changes are blocked' : 'Price changes are allowed'}
                          </p>
                        </div>

                        {/* Averaging Window */}
                        <div>
                          <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Averaging Window (weeks)</label>
                          <input
                            type="number"
                            value={pricingConfig.averaging_window_weeks}
                            onChange={(e) => handleUpdatePricingConfig({ averaging_window_weeks: parseInt(e.target.value) })}
                            className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg"
                            min="1"
                            max="52"
                          />
                        </div>

                        {/* Profit Multiplier */}
                        <div>
                          <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Profit Multiplier</label>
                          <input
                            type="number"
                            step="0.001"
                            value={pricingConfig.profit_multiplier}
                            onChange={(e) => handleUpdatePricingConfig({ profit_multiplier: parseFloat(e.target.value) })}
                            className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg"
                          />
                        </div>

                        {/* Max Weekly Change */}
                        <div>
                          <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Max Weekly Change (%)</label>
                          <input
                            type="number"
                            step="0.1"
                            value={pricingConfig.max_weekly_change_percent}
                            onChange={(e) => handleUpdatePricingConfig({ max_weekly_change_percent: parseFloat(e.target.value) })}
                            className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg"
                          />
                        </div>

                        {/* Price Floor */}
                        <div>
                          <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Price Floor ($)</label>
                          <input
                            type="number"
                            value={pricingConfig.price_floor || ''}
                            onChange={(e) => handleUpdatePricingConfig({ price_floor: e.target.value ? parseFloat(e.target.value) : null })}
                            placeholder="No minimum"
                            className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg"
                          />
                        </div>

                        {/* Price Ceiling */}
                        <div>
                          <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Price Ceiling ($)</label>
                          <input
                            type="number"
                            value={pricingConfig.price_ceiling || ''}
                            onChange={(e) => handleUpdatePricingConfig({ price_ceiling: e.target.value ? parseFloat(e.target.value) : null })}
                            placeholder="No maximum"
                            className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Auto-Pricing Formula Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                      <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        Auto-Pricing Formula
                      </h4>
                      <p className="text-sm text-blue-700">
                        New Price = Current Price + (Average Net Profit ÷ Total Shares × Multiplier)
                      </p>
                      <p className="text-xs text-blue-600 mt-2">
                        The price is recalculated each time new performance data is added. Changes are capped at {pricingConfig.max_weekly_change_percent}% per update.
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Requests Tab */}
            {activeTab === 'requests' && (
              <div className="bg-white border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <div className="p-6 border-b border-[hsl(var(--border))]">
                  <h2 className="text-lg font-semibold text-[hsl(var(--text-primary))]">Buy Requests ({requests.length})</h2>
                </div>
                {requests.length === 0 ? (
                  <div className="p-12 text-center">
                    <Check className="w-12 h-12 text-[hsl(var(--text-tertiary))] mx-auto mb-4" />
                    <p className="text-[hsl(var(--text-secondary))]">No pending requests</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table data-testid="admin-requests-table" className="w-full">
                      <thead className="bg-[hsl(var(--background-secondary))]">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Employee</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Asset</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Shares</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[hsl(var(--border))]">
                        {requests.map((request) => (
                          <tr key={request.request_id} className="hover:bg-[hsl(var(--background-secondary))]">
                            <td className="px-6 py-4 text-sm text-[hsl(var(--text-primary))]">{formatDateTime(request.created_at)}</td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-[hsl(var(--text-primary))]">{request.user_name}</div>
                              <div className="text-xs text-[hsl(var(--text-tertiary))]">{request.user_email}</div>
                            </td>
                            <td className="px-6 py-4 text-sm text-[hsl(var(--text-primary))]">{request.asset_id}</td>
                            <td className="px-6 py-4 text-sm font-mono text-[hsl(var(--text-primary))]">{formatNumber(request.shares_requested)}</td>
                            <td className="px-6 py-4 text-sm font-mono font-semibold text-[hsl(var(--text-primary))]">{formatCurrency(request.total_amount)}</td>
                            <td className="px-6 py-4"><StatusBadge status={request.status} /></td>
                            <td className="px-6 py-4">
                              {request.status === 'pending' && (
                                <div className="flex gap-2">
                                  <button
                                    data-testid={`approve-request-${request.request_id}`}
                                    onClick={() => handleApproveRequest(request.request_id)}
                                    className="p-1 hover:bg-green-100 rounded"
                                    title="Approve"
                                  >
                                    <Check className="w-4 h-4 text-green-600" />
                                  </button>
                                  <button
                                    data-testid={`reject-request-${request.request_id}`}
                                    onClick={() => handleRejectRequest(request.request_id)}
                                    className="p-1 hover:bg-red-100 rounded"
                                    title="Reject"
                                  >
                                    <X className="w-4 h-4 text-red-600" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Audit Logs Tab */}
            {activeTab === 'audit' && (
              <div className="bg-white border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <div className="p-6 border-b border-[hsl(var(--border))]">
                  <h2 className="text-lg font-semibold text-[hsl(var(--text-primary))]">Audit Logs ({auditLogs.length})</h2>
                </div>
                {auditLogs.length === 0 ? (
                  <div className="p-12 text-center">
                    <AlertCircle className="w-12 h-12 text-[hsl(var(--text-tertiary))] mx-auto mb-4" />
                    <p className="text-[hsl(var(--text-secondary))]">No audit logs yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table data-testid="admin-audit-table" className="w-full">
                      <thead className="bg-[hsl(var(--background-secondary))]">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Timestamp</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">User</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Action</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[hsl(var(--text-secondary))] uppercase">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[hsl(var(--border))]">
                        {auditLogs.map((log) => (
                          <tr key={log.log_id} className="hover:bg-[hsl(var(--background-secondary))]">
                            <td className="px-6 py-4 text-sm text-[hsl(var(--text-primary))]">{formatDateTime(log.timestamp)}</td>
                            <td className="px-6 py-4 text-sm text-[hsl(var(--text-primary))]">{log.user_name}</td>
                            <td className="px-6 py-4 text-sm font-medium text-[hsl(var(--text-primary))]">{log.action.replace('_', ' ').toUpperCase()}</td>
                            <td className="px-6 py-4 text-sm text-[hsl(var(--text-secondary))] max-w-md truncate">{JSON.stringify(log.details)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Add Performance Modal */}
      {showPerformanceForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-2xl w-full my-8">
            <div className="p-6 border-b border-[hsl(var(--border))] sticky top-0 bg-white rounded-t-xl">
              <h2 className="text-xl font-semibold text-[hsl(var(--text-primary))]">Add Daily Performance</h2>
              <p className="text-sm text-[hsl(var(--text-secondary))] mt-1">Enter daily earnings and expense breakdown for this unit</p>
            </div>
            <form onSubmit={handleAddPerformance} className="p-6 space-y-6">
              {/* Date and Earnings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Date *</label>
                  <input
                    type="date"
                    data-testid="perf-date-input"
                    value={performanceForm.date}
                    onChange={(e) => setPerformanceForm({ ...performanceForm, date: e.target.value })}
                    className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Total Earnings ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    data-testid="perf-earnings-input"
                    value={performanceForm.earnings_total}
                    onChange={(e) => setPerformanceForm({ ...performanceForm, earnings_total: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg"
                    required
                  />
                </div>
              </div>
              
              {/* Fee Display - Auto calculated */}
              {performanceForm.earnings_total && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-sm font-semibold text-amber-800">
                        Fee ({selectedAssetData?.fee_percent || 12}% of Earnings)
                      </span>
                      <p className="text-xs text-amber-600 mt-0.5">Auto-calculated from earnings</p>
                    </div>
                    <div className="text-xl font-bold font-mono text-amber-700">
                      -${((parseFloat(performanceForm.earnings_total) || 0) * ((selectedAssetData?.fee_percent || 12) / 100)).toFixed(2)}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Expense Breakdown Section */}
              <div className="border-t border-[hsl(var(--border))] pt-4">
                <h3 className="text-sm font-semibold text-[hsl(var(--text-primary))] mb-4">Expense Breakdown</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[hsl(var(--text-secondary))] mb-1">
                      ELD ($) <span className="text-green-600 font-bold">FIXED</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      data-testid="perf-eld-input"
                      value={performanceForm.expense_eld}
                      onChange={(e) => setPerformanceForm({ ...performanceForm, expense_eld: e.target.value })}
                      placeholder="5.00"
                      className="w-full px-3 py-2 border border-green-300 bg-green-50 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[hsl(var(--text-secondary))] mb-1">Fuel ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      data-testid="perf-fuel-input"
                      value={performanceForm.expense_fuel}
                      onChange={(e) => setPerformanceForm({ ...performanceForm, expense_fuel: e.target.value })}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[hsl(var(--text-secondary))] mb-1">Insurance ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      data-testid="perf-insurance-input"
                      value={performanceForm.expense_insurance}
                      onChange={(e) => setPerformanceForm({ ...performanceForm, expense_insurance: e.target.value })}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[hsl(var(--text-secondary))] mb-1">Tolls ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      data-testid="perf-tolls-input"
                      value={performanceForm.expense_tolls}
                      onChange={(e) => setPerformanceForm({ ...performanceForm, expense_tolls: e.target.value })}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[hsl(var(--text-secondary))] mb-1">Driver Pay ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      data-testid="perf-driver-input"
                      value={performanceForm.expense_driver_pay}
                      onChange={(e) => setPerformanceForm({ ...performanceForm, expense_driver_pay: e.target.value })}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[hsl(var(--text-secondary))] mb-1">
                      Depreciation ($) <span className="text-green-600 font-bold">FIXED</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      data-testid="perf-depreciation-input"
                      value={performanceForm.expense_depreciation}
                      onChange={(e) => setPerformanceForm({ ...performanceForm, expense_depreciation: e.target.value })}
                      placeholder="95.00"
                      className="w-full px-3 py-2 border border-green-300 bg-green-50 rounded-lg text-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-[hsl(var(--text-secondary))] mb-1">Other Expenses ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      data-testid="perf-other-input"
                      value={performanceForm.expense_other}
                      onChange={(e) => setPerformanceForm({ ...performanceForm, expense_other: e.target.value })}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Notes</label>
                <textarea
                  value={performanceForm.notes}
                  onChange={(e) => setPerformanceForm({ ...performanceForm, notes: e.target.value })}
                  placeholder="Optional notes about this day..."
                  rows={2}
                  className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg resize-none"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-[hsl(var(--border))]">
                <button
                  type="button"
                  onClick={() => setShowPerformanceForm(false)}
                  className="px-6 py-2 border border-[hsl(var(--border))] rounded-lg font-medium text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--background-secondary))]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-performance"
                  disabled={submitting}
                  className="px-6 py-2 bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold rounded-lg disabled:opacity-50"
                >
                  {submitting ? <ButtonLoader text="Adding..." /> : 'Add Daily Data'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Price Override Modal */}
      {showPriceOverride && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-[hsl(var(--border))]">
              <h2 className="text-xl font-semibold text-[hsl(var(--text-primary))]">Manual Price Override</h2>
            </div>
            <form onSubmit={handlePriceOverride} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Current Price</label>
                <div className="text-2xl font-bold font-mono text-[hsl(var(--text-primary))]">
                  {formatCurrency(selectedAssetData?.current_price || 0)}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">New Price ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={priceOverrideForm.new_price}
                  onChange={(e) => setPriceOverrideForm({ ...priceOverrideForm, new_price: e.target.value })}
                  placeholder="Enter new price"
                  className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Reason *</label>
                <textarea
                  value={priceOverrideForm.reason}
                  onChange={(e) => setPriceOverrideForm({ ...priceOverrideForm, reason: e.target.value })}
                  placeholder="Explain why you're changing the price..."
                  rows={3}
                  className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg resize-none"
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPriceOverride(false)}
                  className="px-6 py-2 border border-[hsl(var(--border))] rounded-lg font-medium text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--background-secondary))]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-price-override"
                  disabled={submitting}
                  className="px-6 py-2 bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold rounded-lg disabled:opacity-50"
                >
                  {submitting ? <ButtonLoader text="Updating..." /> : 'Update Price'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Asset Modal */}
      {showAssetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[hsl(var(--border))] sticky top-0 bg-white">
              <h2 className="text-xl font-semibold text-[hsl(var(--text-primary))]">
                {editingAsset ? 'Edit Asset' : 'Add New Asset'}
              </h2>
            </div>
            <form onSubmit={handleSaveAsset} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Asset Name *</label>
                  <input
                    type="text"
                    data-testid="asset-name-input"
                    value={assetForm.name}
                    onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })}
                    placeholder="e.g. Freightliner Cascadia 1001"
                    className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg"
                    required
                  />
                </div>
                
                {/* Unit Number */}
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Unit Number *</label>
                  <input
                    type="text"
                    data-testid="asset-unit-input"
                    value={assetForm.unit_number}
                    onChange={(e) => setAssetForm({ ...assetForm, unit_number: e.target.value })}
                    placeholder="e.g. TRK-1001"
                    className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg"
                    required
                  />
                </div>
                
                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Type *</label>
                  <select
                    data-testid="asset-type-select"
                    value={assetForm.type}
                    onChange={(e) => setAssetForm({ ...assetForm, type: e.target.value })}
                    className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-white"
                  >
                    <option value="truck">Truck</option>
                    <option value="trailer">Trailer</option>
                  </select>
                </div>
                
                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Status *</label>
                  <select
                    data-testid="asset-status-select"
                    value={assetForm.status}
                    onChange={(e) => setAssetForm({ ...assetForm, status: e.target.value })}
                    className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-white"
                  >
                    <option value="available">Available</option>
                    <option value="closed">Closed</option>
                    <option value="sold_out">Sold Out</option>
                    <option value="coming_soon">Coming Soon</option>
                  </select>
                </div>
                
                {/* Total Shares */}
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Total Shares *</label>
                  <input
                    type="number"
                    data-testid="asset-shares-input"
                    value={assetForm.total_shares}
                    onChange={(e) => setAssetForm({ ...assetForm, total_shares: parseInt(e.target.value) || 0 })}
                    placeholder="180"
                    min="1"
                    className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg"
                    required
                  />
                </div>
                
                {/* Current Price */}
                <div>
                  <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Price per Share ($) *</label>
                  <input
                    type="number"
                    data-testid="asset-price-input"
                    step="0.01"
                    value={assetForm.current_price}
                    onChange={(e) => setAssetForm({ ...assetForm, current_price: parseFloat(e.target.value) || 0 })}
                    placeholder="1000.00"
                    min="0.01"
                    className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg"
                    required
                  />
                </div>
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Description</label>
                <textarea
                  data-testid="asset-description-input"
                  value={assetForm.description}
                  onChange={(e) => setAssetForm({ ...assetForm, description: e.target.value })}
                  placeholder="Enter asset description..."
                  rows={3}
                  className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg resize-none"
                />
              </div>
              
              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">Image URL</label>
                <input
                  type="url"
                  data-testid="asset-image-input"
                  value={assetForm.image_url}
                  onChange={(e) => setAssetForm({ ...assetForm, image_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg"
                />
              </div>
              
              {/* Allow Buy Requests */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setAssetForm({ ...assetForm, allow_buy_requests: !assetForm.allow_buy_requests })}
                  className={`w-12 h-6 rounded-full transition-colors duration-200 ${assetForm.allow_buy_requests ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${assetForm.allow_buy_requests ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
                <label className="text-sm font-medium text-[hsl(var(--text-primary))]">Allow Buy Requests</label>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-[hsl(var(--border))]">
                <button
                  type="button"
                  onClick={() => setShowAssetModal(false)}
                  className="px-6 py-2 border border-[hsl(var(--border))] rounded-lg font-medium text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--background-secondary))]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-asset-form"
                  disabled={submitting}
                  className="px-6 py-2 bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold rounded-lg disabled:opacity-50"
                >
                  {submitting ? <ButtonLoader text="Saving..." /> : (editingAsset ? 'Update Asset' : 'Create Asset')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
